import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  listOrders,
  reissueEcmsLabel,
  shippingLabelPdfUrl,
} from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import type { OrderDto } from "@/api/types";

const PLATFORMS = ["", "shopee", "lazada", "joom", "shopify", "momo"];
const STATUSES = [
  "",
  "new",
  "allocated",
  "label_ready",
  "printed",
  "shipped",
  "error",
];
const PAGE_SIZE = 50;

export default function OrdersListPage() {
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState("");
  const [country, setCountry] = useState("");
  const [orderNo, setOrderNo] = useState("");
  const [page, setPage] = useState(0);

  const params = useMemo(
    () => ({
      platform: platform || undefined,
      status: status || undefined,
      country_code: country || undefined,
      order_no: orderNo || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [platform, status, country, orderNo, page],
  );

  const q = useQuery({
    queryKey: ["orders", params],
    queryFn: () => listOrders(params),
  });

  const queryClient = useQueryClient();
  const credentials = useAuthStore((s) => s.credentials);
  const [reissueMsg, setReissueMsg] = useState<{
    message: string;
    orderId: string;
    orderNo: string;
  } | null>(null);
  const [reissueErr, setReissueErr] = useState<string | null>(null);

  // ラベル PDF を認証付きで取得して別タブで開く (PrintJobPage と同じパターン。
  // 素の <a href> は Authorization ヘッダが付かず 401 になる)
  const openLabelPdf = async (orderId: string) => {
    try {
      const resp = await fetch(shippingLabelPdfUrl(orderId), {
        headers: credentials ? { Authorization: `Basic ${credentials}` } : {},
      });
      if (!resp.ok) {
        setReissueErr(`ラベル PDF 取得失敗: HTTP ${resp.status}`);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setReissueErr(String(e));
    }
  };

  // ECMS ラベル再発行 (2026-07-22): 発送後に受取人名の誤りが判明したケース用。
  // PDF は成功メッセージ内のリンクから開く (非同期完了後の window.open は
  // ブラウザのポップアップブロックに引っかかるため使わない)。
  const reissueMut = useMutation({
    mutationFn: (v: { orderId: string; name?: string; phone?: string }) =>
      reissueEcmsLabel(v.orderId, v.name, v.phone),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      setReissueErr(null);
      setReissueMsg({
        message: r.message,
        orderId: r.id,
        orderNo: r.order_no,
      });
    },
    onError: (e) => {
      setReissueMsg(null);
      setReissueErr(e instanceof ApiError ? e.message : String(e));
    },
  });

  const handleReissue = (o: OrderDto) => {
    const name = prompt(
      `注文 ${o.order_no} の ECMS ラベルを再発行します。\n` +
        `【1/2】受取人名を変更する場合は新しい名前を入力してください\n` +
        `(変更しない場合はそのまま OK):`,
      o.recipient_name,
    );
    if (name === null) return; // キャンセル
    const phone = prompt(
      `【2/2】電話番号を変更する場合は新しい番号を入力してください\n` +
        `(変更しない場合はそのまま OK):`,
      o.recipient_phone ?? "",
    );
    if (phone === null) return; // キャンセル
    const nameTrimmed = name.trim();
    const phoneTrimmed = phone.trim();
    const newName =
      nameTrimmed && nameTrimmed !== o.recipient_name ? nameTrimmed : undefined;
    const newPhone =
      phoneTrimmed && phoneTrimmed !== (o.recipient_phone ?? "")
        ? phoneTrimmed
        : undefined;
    if (
      !confirm(
        `ECMS に新しい shipment を作成します。\n` +
          `受取人名: ${newName ?? `${o.recipient_name} (変更なし)`}\n` +
          `電話番号: ${newPhone ?? `${o.recipient_phone ?? "-"} (変更なし)`}\n` +
          `発行後、旧トラッキングの ECMS 管理画面でのキャンセルが必要です。実行しますか？`,
      )
    )
      return;
    reissueMut.mutate({
      orderId: o.id,
      name: newName,
      phone: newPhone,
    });
  };

  const totalPages = q.data ? Math.ceil(q.data.total / PAGE_SIZE) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-brand-primary">受注一覧</h2>
        <p className="text-sm text-gray-500 mt-1">
          全プラットフォームの受注をフィルタ・検索できます。
        </p>
      </div>

      <div className="card space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="label">プラットフォーム</label>
            <select
              className="input"
              value={platform}
              onChange={(e) => {
                setPlatform(e.target.value);
                setPage(0);
              }}
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>{p || "(すべて)"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">ステータス</label>
            <select
              className="input"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s || "(すべて)"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">配送先国コード</label>
            <input
              className="input"
              value={country}
              placeholder="sg / my / tw"
              onChange={(e) => {
                setCountry(e.target.value);
                setPage(0);
              }}
            />
          </div>
          <div>
            <label className="label">注文番号（完全一致）</label>
            <input
              className="input"
              value={orderNo}
              onChange={(e) => {
                setOrderNo(e.target.value);
                setPage(0);
              }}
            />
          </div>
        </div>
      </div>

      {reissueErr && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
          {reissueErr}
        </div>
      )}
      {reissueMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded space-y-2">
          <p>{reissueMsg.message}</p>
          <button
            className="inline-block btn-accent text-sm"
            onClick={() => openLabelPdf(reissueMsg.orderId)}
          >
            📄 新ラベル PDF を開く ({reissueMsg.orderNo})
          </button>
        </div>
      )}

      <div className="card overflow-x-auto">
        {q.isLoading && <p className="text-gray-500">読み込み中…</p>}
        {q.error && (
          <p className="text-red-600">取得失敗: {String(q.error)}</p>
        )}
        {q.data && (
          <>
            <div className="flex items-center justify-between mb-3 text-sm">
              <span className="text-gray-500">
                合計 {q.data.total} 件中 {q.data.orders.length} 件を表示
              </span>
              <div className="flex items-center gap-2">
                <button
                  className="btn-secondary text-xs"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  前へ
                </button>
                <span className="text-gray-600">
                  {page + 1} / {Math.max(1, totalPages)}
                </span>
                <button
                  className="btn-secondary text-xs"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  次へ
                </button>
              </div>
            </div>
            <table className="min-w-full text-sm">
              <thead className="border-b text-left text-gray-500">
                <tr>
                  <th className="py-2 pr-3">プラットフォーム</th>
                  <th className="py-2 pr-3">注文番号</th>
                  <th className="py-2 pr-3">パッキング</th>
                  <th className="py-2 pr-3">追跡番号</th>
                  <th className="py-2 pr-3">受取人</th>
                  <th className="py-2 pr-3">国</th>
                  <th className="py-2 pr-3">ステータス</th>
                  <th className="py-2 pr-3">注文日時</th>
                  <th className="py-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {q.data.orders.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-6 text-center text-gray-400">
                      該当する受注がありません
                    </td>
                  </tr>
                )}
                {q.data.orders.map((o: OrderDto) => (
                  <tr key={o.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 pr-3">
                      <span className="font-medium">{o.platform}</span>
                      <span className="ml-1 text-xs text-gray-500">/{o.country_code}</span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{o.order_no}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-gray-600">
                      {o.packing_no ?? "-"}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs text-gray-600">
                      {o.tracking_no ?? "-"}
                    </td>
                    <td className="py-2 pr-3 truncate max-w-xs">{o.recipient_name}</td>
                    <td className="py-2 pr-3 text-xs">{o.recipient_country}</td>
                    <td className="py-2 pr-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          o.status === "shipped"
                            ? "bg-green-100 text-green-800"
                            : o.status === "printed"
                              ? "bg-blue-100 text-blue-800"
                              : o.status === "error"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {o.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-500">
                      {o.order_date ? new Date(o.order_date).toLocaleString("ja-JP") : "-"}
                    </td>
                    <td className="py-2">
                      {o.logistics_type === "ecms" && o.tracking_no && (
                        <button
                          className="btn-secondary text-xs whitespace-nowrap"
                          disabled={reissueMut.isPending}
                          onClick={() => handleReissue(o)}
                          title="受取人名の変更などで ECMS ラベルを -R 枝番で再発行します"
                        >
                          ECMSラベル再発行
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
